import React, {useLayoutEffect} from "react";
import {setTemplateScopeMeta} from "libs/logoOverrides";
import {isBlackLogoUrl} from "libs/logo";
import {LOGO_VARIANT_NONE} from "libs/logoVariantConstants";
import {useImages} from "contexts/images";
import {Setting, useSetting} from "contexts/setting";
import {isScopesChannel} from "libs/sceneScope";
import {textColorStyle} from "libs/textColor";

const LOGO_CLASS_PREFIX = "logo-";

/**
 * Extract semantic CSS classes from a logo URL filename.
 * e.g. "JetBrains_Academy_logo_black.svg"    → ["logo-academy", "logo-black"]
 *      "JETBRAINS_IDEs_logo_white.svg"        → ["logo-ides", "logo-white"]
 *      "JetBrains_Foundation_logo_black_inverted_two.svg" → ["logo-foundation", "logo-black", "logo-inverted", "logo-two"]
 *      "Junie_logo_white.svg"                 → ["logo-junie", "logo-white"]
 */
const extractLogoClasses = (url) => {
  if (url === LOGO_VARIANT_NONE) {
    return ["logo-none"];
  }
  if (!url) return ["logo-auto"];
  // Get filename without extension: ".../Logos/JetBrains_Academy_logo_black.svg" → "JetBrains_Academy_logo_black"
  const filename =
    url
      .split("/")
      .pop()
      ?.replace(/\.\w+$/, "") || "";
  if (!filename) return ["logo-auto"];

  // Split on "_logo_" or "_icon" to separate brand from modifiers
  const logoSplit = filename.split(/_logo_|_icon/);
  const brandPart = (logoSplit[0] || "").toLowerCase();
  const modifierPart = (logoSplit[1] || "").toLowerCase();

  const classes = [];

  // Extract brand: strip "jetbrains_" prefix, take the meaningful part
  const brand = brandPart
    .replace(/^jetbrains_?/i, "")
    .replace(/^jb_?/i, "")
    .replace(/_/g, "-");
  if (brand) {
    classes.push(LOGO_CLASS_PREFIX + brand);
  } else {
    classes.push(LOGO_CLASS_PREFIX + "jetbrains");
  }

  // Extract modifiers: white, black, inverted, outlined, two, transparent, minimal, etc.
  if (modifierPart) {
    modifierPart
      .split("_")
      .filter(Boolean)
      .forEach((mod) => {
        classes.push(LOGO_CLASS_PREFIX + mod);
      });
  }

  return classes;
};

const TEXT_COLOR_VAR = "--logo-override-text-color";

/** True for a class that TemplateScope gives the .template element. */
const isTemplateScopeClass = (c) =>
  c.startsWith(LOGO_CLASS_PREFIX) || c === "text-color-locked" || c === "text-color-chosen";

/**
 * The classes and the text colour of one banner, from its logo (`variant`, a
 * value of `logoVariants`) and its text colour (a value of `textColorLocked`).
 *
 * - The logo classes come from the logo URL (`logo-auto` without a URL).
 * - A text colour lock ("black", "light", or a colour chosen in the Defaults
 *   panel, libs/textColor.ts) freezes the text colour. The product name
 *   stays in the colour of the logo, unless the colour was chosen.
 * - Without a lock, a logo of a manual choice sets the text colour.
 *
 * `textColor` is the CSS value of the lock: the inline value is stronger than
 * the black and light classes. `blackLogo` marks the `.template-wrap`.
 */
export function templateScopeClasses(variant, textColorLock) {
  const isNoLogo = variant === LOGO_VARIANT_NONE;
  const isManualOverride = typeof variant === "string" && variant.length > 0 && !isNoLogo;
  const isBlack = isManualOverride && isBlackLogoUrl(variant);
  const lockedStyle = textColorStyle(textColorLock);

  const classes = new Set(extractLogoClasses(variant));
  if (isBlack) classes.add("logo-black");
  else classes.delete("logo-black");
  if (lockedStyle) {
    classes.add("text-color-locked");
    if (lockedStyle.coversProductName) classes.add("text-color-chosen");
    classes.add("logo-override-active");
    classes.add(lockedStyle.dark ? "logo-override-black" : "logo-override-light");
  } else if (isManualOverride) {
    classes.add("logo-override-active");
    classes.add(isBlack ? "logo-override-black" : "logo-override-light");
  }
  return {classes, textColor: lockedStyle?.color ?? null, blackLogo: isBlack};
}

/**
 * Give the .template element the classes of `templateScopeClasses` and remove
 * the other TemplateScope classes. Only a class or a value that differs is
 * written, so a call that changes nothing causes no style recalculation.
 */
export function syncTemplateScopeClasses(el, {classes, textColor, blackLogo}) {
  for (const c of [...el.classList]) {
    if (isTemplateScopeClass(c) && !classes.has(c)) el.classList.remove(c);
  }
  for (const c of classes) {
    if (!el.classList.contains(c)) el.classList.add(c);
  }
  if (textColor === null) {
    if (el.style.getPropertyValue(TEXT_COLOR_VAR)) el.style.removeProperty(TEXT_COLOR_VAR);
  } else if (el.style.getPropertyValue(TEXT_COLOR_VAR) !== textColor) {
    el.style.setProperty(TEXT_COLOR_VAR, textColor);
  }
  const wrap = el.closest(".template-wrap");
  if (wrap && wrap.classList.contains("blacklogo") !== blackLogo) {
    wrap.classList.toggle("blacklogo", blackLogo);
  }
}

export const createTemplateRenderSurface = (id, element, width, height, zoom) => {
  if (!React.isValidElement(element)) {
    throw new Error("TemplateScope requires one React element");
  }
  return (
    <div
      className="template-render-surface"
      data-template-id={id}
      data-template-width={width}
      data-template-height={height}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        "--zoom-level": zoom,
      }}
    >
      {element}
    </div>
  );
};

const TemplateScope = ({id, templateId, width, height, zoom, children}) => {
  useLayoutEffect(() => {
    const element = document.getElementById(id);
    if (element) element.dataset.templateCanonicalId = templateId;
  }, [id, templateId]);
  const {logoVariants, textColorLocked, logoHidden, fieldColors, elementColors} = useImages();
  const setting = useSetting();
  const {product, type, channel, layouts, variants, dataChannel} = setting;
  // The hide-logo control is a Social-channel feature. A product that
  // carries its own offering answers to that offering, so Air draws the same
  // banner through either entry.
  const isSocialChannel = dataChannel === "social";

  // Scene scopes: a banner with a layout or variant of its own (own or
  // preset) renders under a Setting that says so, the way the ALL-products
  // view overrides `product` per child. Every template keeps reading
  // `layoutOption` from useSetting and needs no change.
  const scoped = isScopesChannel(dataChannel);
  const ownLayout = scoped ? layouts?.[id] : undefined;
  const ownVariant = scoped ? variants?.[id] : undefined;
  const layoutOption = ownLayout ?? setting.layoutOption;
  const variantOption = ownVariant ?? setting.variantOption;

  setTemplateScopeMeta(id, {
    productValue: product?.value,
    typeValue: type?.value,
    layoutOption,
    channelValue: channel?.value,
  });

  // The text colour and the logo classes of the .template element.
  //
  // The template owns the `className` of that element, and React writes the
  // whole attribute again each time the class name changes. A banner that gets
  // a layout or a variant of its own changes only its class name, so these
  // records stay the same objects. An effect that ran only when the records
  // change thus lost the classes: the text of the banner went white on a light
  // background (SoMe Square on RustRover Releases, 2026-11-03). The effect runs
  // after each render and changes only the classes that differ. It is a layout
  // effect, so the banner never paints without its classes.
  useLayoutEffect(() => {
    const el = document.getElementById(id);
    if (!el) return;
    const variant = isSocialChannel && logoHidden ? LOGO_VARIANT_NONE : logoVariants[id];
    syncTemplateScopeClasses(el, templateScopeClasses(variant, textColorLocked[id]));
  });

  const surface = createTemplateRenderSurface(id, children, width, height, zoom);
  const scopedSetting = {...setting, layoutOption, variantOption, templateId: templateId ?? id};
  return (
    <div className="template-preview-scale" data-color-cascade={Object.hasOwn(fieldColors, id) || Object.hasOwn(elementColors, id) ? "2" : undefined} style={{"--zoom-level": zoom}}>
      <Setting.Provider value={scopedSetting}>{surface}</Setting.Provider>
    </div>
  );
};

export default TemplateScope;
